/**
 * useAIChatStream — AG-UI hook for the Forecast AI chat panel.
 *
 * Uses @ag-ui/client HttpAgent for the full AG-UI event lifecycle.
 * Gateway URL and auth header are configured via constants at the top of
 * this file. The hook owns all message + streaming state.
 */
import {useState, useCallback, useRef, useEffect, useLayoutEffect} from 'react';
import type {Dispatch, SetStateAction} from 'react';
import {HttpAgent} from '@ag-ui/client';
import type {RunAgentInput} from '@ag-ui/client';
import {v4 as uuid} from 'uuid';

// HttpAgent does not expose a credentials option, so we subclass to add it.
class CredentialedHttpAgent extends HttpAgent {
	protected override requestInit(input: RunAgentInput): RequestInit {
		return {...super.requestInit(input), credentials: 'include'};
	}
}

// Process is provided by webpack DefinePlugin at build time
declare const process: {
	env: {
		REACT_APP_AI_DEPLOY_VERSION: string;
		REACT_APP_AI_GATEWAY_URL?: string;
	};
};

const GATEWAY = process.env.REACT_APP_AI_GATEWAY_URL || 'http://localhost:7861';
const FORECAST_CHAT_AGENT_ID = 'forecast_chat';
const USER_APPROVAL_TOOL = 'user_approval_tool';

const SESSION_MESSAGES_KEY = 'ai-chat-messages';
const SESSION_THREAD_KEY = 'ai-chat-thread-id';
const SESSION_DEPLOY_KEY = 'ai-chat-deploy-version';

// Clear stale sessionStorage state when a new deployment is detected.
// REACT_APP_AI_DEPLOY_VERSION is the CI commit hash (empty in local dev).
// An empty version means we can't detect deployments — skip the check.
const DEPLOY_VERSION = process.env.REACT_APP_AI_DEPLOY_VERSION || '';

if (DEPLOY_VERSION) {
	try {
		const stored = sessionStorage.getItem(SESSION_DEPLOY_KEY);
		if (stored !== DEPLOY_VERSION) {
			sessionStorage.removeItem(SESSION_MESSAGES_KEY);
			sessionStorage.removeItem(SESSION_THREAD_KEY);
			sessionStorage.setItem(SESSION_DEPLOY_KEY, DEPLOY_VERSION);
		}
	} catch (_) {
		// no-op — sessionStorage may be unavailable
	}
}

// ── Domain types ──────────────────────────────────────────────────────────────

export type StatusStep = {
	id: string;
	stepType: 'thinking' | 'collating' | 'tool';
	done: boolean;
	toolName?: string;
	input?: Record<string, unknown>;
	output?: string;
	outputTruncated?: boolean;
};

export type Message = {
	id: string;
	role: 'user' | 'assistant';
	content: string;
	pending?: boolean;
	statusSteps?: StatusStep[];
	createdAt?: string;
	error?: string;
};

type UseAIChatStreamReturn = {
	messages: Message[];
	setMessages: Dispatch<SetStateAction<Message[]>>;
	isStreaming: boolean;
	isHitlPending: boolean;
	sendMessage: (userText: string, allMessages: Message[]) => Promise<void>;
	abort: () => void;
	clearMessages: () => void;
};

// ── Local AG-UI subscriber types ──────────────────────────────────────────────
// Mirrors the relevant subset of AgentSubscriber from @ag-ui/client.
// Defined locally so TypeScript can infer handler parameter types without
// requiring the IDE to resolve @ag-ui/client from node_modules/.ignored/.

type StepEvt = {stepName: string};
type ToolCallStartEvt = {toolCallId: string; toolCallName: string};
type ToolCallEndEvt = {toolCallId: string};
type ToolCallResultEvt = {toolCallId: string; content: string};
type TextMessageContentEvt = {delta: string};
type RunErrorEvt = {message: string};

type AIChatSubscriber = {
	onStepStartedEvent?: (params: {event: StepEvt}) => void;
	onStepFinishedEvent?: (params: {event: StepEvt}) => void;
	onToolCallStartEvent?: (params: {event: ToolCallStartEvt}) => void;
	onToolCallEndEvent?: (params: {event: ToolCallEndEvt; toolCallArgs: Record<string, unknown>}) => void;
	onToolCallResultEvent?: (params: {event: ToolCallResultEvt}) => void;
	onTextMessageStartEvent?: () => void;
	onTextMessageContentEvent?: (params: {event: TextMessageContentEvt}) => void;
	onRunFinishedEvent?: () => void;
	onRunErrorEvent?: (params: {event: RunErrorEvt}) => void;
	onRunFailed?: (params: {error: Error}) => void;
};

// ── Persistence helpers ───────────────────────────────────────────────────────

function readPersistedMessages(): Message[] {
	try {
		const raw = sessionStorage.getItem(SESSION_MESSAGES_KEY);
		return raw ? (JSON.parse(raw) as Message[]) : [];
	} catch (_) {
		return [];
	}
}

function readOrCreateThreadId(): string {
	try {
		const stored = sessionStorage.getItem(SESSION_THREAD_KEY);
		if (stored) return stored;
		const id = uuid();
		sessionStorage.setItem(SESSION_THREAD_KEY, id);
		return id;
	} catch (_) {
		return uuid();
	}
}

// ── Tool label lookup ─────────────────────────────────────────────────────────

// Tools emitted by the gateway that are internal signals — never shown in UI.
enum SentinelTool {
	CreateProjectIntent = 'create_project_intent',
}
const SENTINEL_TOOLS: Set<string> = new Set(Object.values(SentinelTool));

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAIChatStream({
	apiKey,
	viewerPersonId,
	companyId,
	companyName,
	onProjectIntent,
}: {
	apiKey: string;
	viewerPersonId?: number;
	companyId?: string;
	companyName?: string;
	onProjectIntent?: (data: Record<string, unknown>) => void;
}): UseAIChatStreamReturn {
	const [messages, setMessages] = useState<Message[]>(readPersistedMessages);
	const [isStreaming, setIsStreaming] = useState(false);
	const [isHitlPending, setIsHitlPending] = useState(false);
	const agentRef = useRef<HttpAgent | null>(null);
	const stepsRef = useRef<StatusStep[]>([]);
	const prevApiKeyRef = useRef<string | null>(null);
	const viewerPersonIdRef = useRef<number | undefined>(viewerPersonId);
	viewerPersonIdRef.current = viewerPersonId;
	const companyIdRef = useRef<string | undefined>(companyId);
	companyIdRef.current = companyId;
	const companyNameRef = useRef<string | undefined>(companyName);
	companyNameRef.current = companyName;
	const onProjectIntentRef = useRef<((data: Record<string, unknown>) => void) | undefined>(onProjectIntent);
	onProjectIntentRef.current = onProjectIntent;

	// (Re-)init the HttpAgent when apiKey first becomes available or changes.
	// useLayoutEffect (synchronous, pre-paint) preserves the timing guarantee
	// of the previous render-phase init without being a side-effect-in-render.
	useLayoutEffect(() => {
		if (agentRef.current && (!apiKey || apiKey === prevApiKeyRef.current)) return;
		prevApiKeyRef.current = apiKey;
		const headers: Record<string, string> = {};
		if (apiKey) headers['X-Forecast-Api-Key'] = apiKey;
		if (viewerPersonIdRef.current != null) headers['X-Viewer-Person-Id'] = String(viewerPersonIdRef.current);
		// Tenant headers for telemetry. Omit entirely when unavailable —
		// the gateway treats absent and empty headers differently.
		if (companyIdRef.current) headers['X-Forecast-Company-Id'] = companyIdRef.current;
		if (companyNameRef.current) headers['X-Forecast-Company-Name'] = companyNameRef.current;
		const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		if (userTimeZone) headers['X-User-Timezone'] = userTimeZone;
		headers['x-agent-id'] = FORECAST_CHAT_AGENT_ID;
		agentRef.current = new CredentialedHttpAgent({
			url: `${GATEWAY}/invocations`,
			threadId: agentRef.current ? agentRef.current.threadId : readOrCreateThreadId(),
			headers,
		});
	}, [apiKey]);

	useEffect(() => {
		try {
			const toStore = messages.filter((m: Message) => !m.pending);
			sessionStorage.setItem(SESSION_MESSAGES_KEY, JSON.stringify(toStore));
		} catch (_) {
			// no-op — sessionStorage may be unavailable
		}
	}, [messages]);

	// ── Step state helpers (sync ref mutations, no re-render until syncSteps) ──
	function resetSteps(): void {
		stepsRef.current = [];
	}
	function pushStep(step: Omit<StatusStep, 'id' | 'done'>): string {
		const id = uuid();
		stepsRef.current = [...stepsRef.current, {id, done: false, ...step}];
		return id;
	}
	function updateStep(id: string, patch: Partial<StatusStep>): void {
		stepsRef.current = stepsRef.current.map((s: StatusStep) => (s.id === id ? {...s, ...patch} : s));
	}
	function doneAll(): void {
		stepsRef.current = stepsRef.current.map((s: StatusStep) => ({...s, done: true}));
	}
	function syncSteps(msgId: string): void {
		const snapshot = [...stepsRef.current];
		setMessages((prev: Message[]) => prev.map((m: Message) => (m.id === msgId ? {...m, statusSteps: snapshot} : m)));
	}

	// ── Clear ─────────────────────────────────────────────────────────────────
	const clearMessages = useCallback(() => {
		// Abort any in-flight stream before wiping state
		if (agentRef.current) agentRef.current.abortRun();
		setIsStreaming(false);
		setIsHitlPending(false);
		try {
			sessionStorage.removeItem(SESSION_MESSAGES_KEY);
			sessionStorage.removeItem(SESSION_THREAD_KEY);
		} catch (_) {
			// no-op — sessionStorage may be unavailable
		}
		setMessages([]);
		// Re-init agent with a fresh thread ID so LLM context is also reset
		const newThreadId = uuid();
		try {
			sessionStorage.setItem(SESSION_THREAD_KEY, newThreadId);
		} catch (_) {
			// no-op — sessionStorage may be unavailable
		}
		const headers: Record<string, string> = {};
		const key = prevApiKeyRef.current;
		if (key) headers['X-Forecast-Api-Key'] = key;
		if (viewerPersonIdRef.current != null) headers['X-Viewer-Person-Id'] = String(viewerPersonIdRef.current);
		if (companyIdRef.current) headers['X-Forecast-Company-Id'] = companyIdRef.current;
		if (companyNameRef.current) headers['X-Forecast-Company-Name'] = companyNameRef.current;
		const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		if (userTimeZone) headers['X-User-Timezone'] = userTimeZone;
		headers['x-agent-id'] = FORECAST_CHAT_AGENT_ID;
		agentRef.current = new CredentialedHttpAgent({
			url: `${GATEWAY}/invocations`,
			threadId: newThreadId,
			headers,
		});
	}, []);

	// ── Abort ─────────────────────────────────────────────────────────────────
	const abort = useCallback(() => {
		if (agentRef.current) agentRef.current.abortRun();
	}, []);

	// ── Send ──────────────────────────────────────────────────────────────────
	const sendMessage = useCallback(
		async (userText: string, allMessages: Message[]): Promise<void> => {
			if (isStreaming) return;

			setIsStreaming(true);
			resetSteps();

			const assistantMsgId = uuid();
			setMessages((prev: Message[]) => [
				...prev,
				{
					id: uuid(),
					role: 'user',
					content: userText,
					createdAt: new Date().toISOString(),
				},
				{
					id: assistantMsgId,
					role: 'assistant',
					content: '',
					pending: true,
					statusSteps: [],
					createdAt: new Date().toISOString(),
				},
			]);

			let thinkingId: string | null = null;
			const toolCallStepMap = new Map<string, string>(); // toolCallId → stepId
			const sentinelToolCallMap = new Map<string, string>(); // toolCallId → toolName (for sentinel tools)
			let textStarted = false;
			let finished = false;

			const finish = (patch: Partial<Message> = {}): void => {
				if (finished) return;
				finished = true;
				doneAll();
				syncSteps(assistantMsgId);
				setIsHitlPending(false);
				setMessages((prev: Message[]) =>
					prev.map((m: Message) => (m.id === assistantMsgId ? {...m, pending: false, ...patch} : m))
				);
				setIsStreaming(false);
			};

			const agent = agentRef.current;
			if (!agent) {
				finish({error: 'Agent not initialised'});
				return;
			}

			agent.setMessages([
				...allMessages
					.filter((m: Message) => !m.pending)
					.map((m: Message) => ({id: m.id, role: m.role, content: m.content})),
				{id: uuid(), role: 'user', content: userText},
			] as Parameters<HttpAgent['setMessages']>[0]);

			const subscriber: AIChatSubscriber = {
				onStepStartedEvent({event}: {event: StepEvt}) {
					const stepName = event && event.stepName;
					if (stepName === 'call_model' && !thinkingId) {
						thinkingId = pushStep({stepType: 'thinking'});
						syncSteps(assistantMsgId);
					}
				},
				onStepFinishedEvent({event}: {event: StepEvt}) {
					const stepName = event && event.stepName;
					if (stepName === 'call_model' && thinkingId) {
						updateStep(thinkingId, {done: true});
						thinkingId = null;
						syncSteps(assistantMsgId);
					}
				},
				onToolCallStartEvent({event}: {event: ToolCallStartEvt}) {
					if (event.toolCallName === USER_APPROVAL_TOOL) setIsHitlPending(true);
					if (SENTINEL_TOOLS.has(event.toolCallName)) {
						sentinelToolCallMap.set(event.toolCallId, event.toolCallName);
						return;
					}
					if (thinkingId) {
						updateStep(thinkingId, {done: true});
						thinkingId = null;
					}
					const toolName = event.toolCallName;
					const stepId = pushStep({stepType: 'tool', toolName});
					toolCallStepMap.set(event.toolCallId, stepId);
					syncSteps(assistantMsgId);
				},
				onToolCallEndEvent({event, toolCallArgs}: {event: ToolCallEndEvt; toolCallArgs: Record<string, unknown>}) {
					const stepId = toolCallStepMap.get(event.toolCallId);
					if (stepId) {
						updateStep(stepId, {input: toolCallArgs});
						syncSteps(assistantMsgId);
					}
				},
				onToolCallResultEvent({event}: {event: ToolCallResultEvt}) {
					const sentinelName = sentinelToolCallMap.get(event.toolCallId);
					if (sentinelName) {
						sentinelToolCallMap.delete(event.toolCallId);
						if (sentinelName === SentinelTool.CreateProjectIntent && onProjectIntentRef.current) {
							try {
								const data = JSON.parse(event.content) as Record<string, unknown>;
								onProjectIntentRef.current(data);
							} catch {
								// ignore parse errors — intent still fired, modal opens without prefill
							}
						}
						return;
					}
					const stepId = toolCallStepMap.get(event.toolCallId);
					if (stepId) {
						const content = event.content;
						updateStep(stepId, {
							done: true,
							output: content,
							outputTruncated: content.includes('[... output truncated at'),
						});
						toolCallStepMap.delete(event.toolCallId);
						syncSteps(assistantMsgId);
					}
				},
				onTextMessageStartEvent() {
					doneAll();
					const colId = pushStep({stepType: 'collating'});
					updateStep(colId, {done: true});
					syncSteps(assistantMsgId);
				},
				onTextMessageContentEvent({event}: {event: TextMessageContentEvt}) {
					if (!textStarted) {
						textStarted = true;
						if (!stepsRef.current.some((s: StatusStep) => s.stepType === 'collating')) {
							doneAll();
							const colId = pushStep({stepType: 'collating'});
							updateStep(colId, {done: true});
							syncSteps(assistantMsgId);
						}
					}
					const delta = event.delta || '';
					setMessages((prev: Message[]) =>
						prev.map((m: Message) =>
							m.id === assistantMsgId ? {...m, content: m.content + delta, pending: false} : m
						)
					);
				},
				onRunFinishedEvent() {
					finish();
				},
				onRunErrorEvent({event}: {event: RunErrorEvt}) {
					finish({error: event.message || 'An error occurred'});
				},
				onRunFailed({error}: {error: Error}) {
					if (error.name === 'AbortError') {
						finish({error: 'stopped'});
					} else {
						finish({error: error.message || 'Connection error'});
					}
				},
			};

			try {
				await agent.runAgent(
					{forwardedProps: {agent_id: FORECAST_CHAT_AGENT_ID}},
					subscriber as Parameters<HttpAgent['runAgent']>[1]
				);
			} catch (err) {
				if (err instanceof Error && err.name === 'AbortError') {
					finish({error: 'stopped'});
				} else {
					finish({error: err instanceof Error ? err.message : 'Connection error'});
				}
			} finally {
				setIsStreaming(false);
			}
		},
		[isStreaming, apiKey]
	);

	return {messages, setMessages, isStreaming, isHitlPending, sendMessage, abort, clearMessages};
}
